import React, {useEffect, useState} from "react";
import {
    ButtonsContainer,
    FormButton,
    FormContent,
    FormHeader,
    FormInput,
    FormLabel,
    FormSubheader,
    StyledForm,
    StyledRow,
    StyledTextArea,
    FileInput,
    ImgPreview,
    ImgPreviewContainer, ImgPreviewDiv, StyledOption, FormSelect, ColoredStatus, StyledInputRow, StyledButton,
} from "./ManageDataForm.styles";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faFutbol, faNewspaper, faTrashAlt, faTrophy, faUsers} from "@fortawesome/free-solid-svg-icons";
import PropTypes from 'prop-types';
import './ManageDataForm.css';
import {addData, fetchData} from "../../helpers/fetchData";
import imageCompression from 'browser-image-compression';

export const rounds = ['Faza grupowa', 'Ćwierćfinał', 'Półfinał', 'Mecz o 3. miejsce', 'Finał'];

const ManageDataForm = ({formType, sendDataToParent}) => {

    const formPhases = {
        initial: {
            color: '#808080',
            text: 'Wypełnij wszystkie wymagane dane.'
        },
        success: {
            color: '#32CD32',
            text: 'Pomyślnie dodano obiekt do bazy danych.'
        },
        error: {
            color: '#DC143C',
            text: 'Wystąpił błąd podczas dodawania obiektu.'
        }
    }

    const controller = new AbortController();
    const signal = controller.signal;
    const [data, setData] = useState({});
    const [images, setImages] = useState([]);
    const [formPhase, setFormPhase] = useState(formPhases.initial);
    const [tournamentsList, setTournamentsList] = useState([]);
    const [playerList, setPlayerList] = useState([]);
    const [goalsInfo, setGoalsInfo] = useState([]);

    const defaultData = {
        players: {position: 'goalkeeper', country: 'pl'},
        tournaments: {place: '1'},
        matches: {tournament_id: '', enemy_goals: 0, round: 0}
    }

    useEffect(() => {
        setFormPhase(formPhases.initial);

        if (formType === 'matches') {
            Promise.all([
                fetchData('tournaments', signal)
                    .then(res => setTournamentsList(res))
                    .catch((error) => {
                        if (error.name !== 'AbortError') {
                            setFormPhase(formPhases.error)
                        }
                    }),
                fetchData('players', signal)
                    .then(res => setPlayerList(res))
                    .catch((error) => {
                        if (error.name !== 'AbortError') {
                            setFormPhase(formPhases.error)
                        }
                    })
            ])
                .catch(() => setFormPhase(formPhases.error));

            return () => {
                controller.abort();
                setTournamentsList([]);
                setPlayerList([]);
            };
        }

        clearData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formType]);

    const formInfo = {
        posts: {
            header: 'Dodawanie posta',
            icon: faNewspaper
        },
        players: {
            header: 'Dodawanie zawodnika',
            icon: faUsers
        },
        matches: {
            header: 'Dodawanie meczu',
            icon: faFutbol
        },
        tournaments: {
            header: 'Dodawanie turnieju',
            icon: faTrophy
        }
    }

    const renderImagesPreview = () => {
        if (!images?.length) {
            return <div/>;
        }
        let view = [];
        for (let i = 0; i < images.length; i++) {
            view.push(
                <ImgPreviewDiv key={images[i].name + i}>
                    <FontAwesomeIcon icon={faTrashAlt} type={'delete'} color={'red'} className={'icon'}
                                     onClick={() => setImages(images.filter(e => e.name !== images[i].name))}/>
                    <ImgPreview src={URL.createObjectURL(images[i])} alt={images[i].name}/>
                </ImgPreviewDiv>
            );
        }

        return view;
    }

    const confirmData = () => {
        switch (formType) {
            case 'posts':
                addPosts().then(finishFetch).catch(() => setFormPhase(formPhases.error));
                break;
            case 'players':
                addPlayers().then(finishFetch).catch(() => setFormPhase(formPhases.error));
                break;
            case 'matches':
                addMatches().then(finishFetch).catch(() => setFormPhase(formPhases.error));
                break;
            case 'tournaments':
                addTournaments().then(finishFetch).catch(() => setFormPhase(formPhases.error));
                break;
            default:
                return true;
        }
    }

    const finishFetch = (res) => {
        let responseResult;

        if (Array.isArray(res)) {
            responseResult = res.every(e => e.ok === true);
        } else {
            responseResult = res.ok;
        }

        if (responseResult) {
            clearData();
            sendDataToParent(responseResult);
            setFormPhase(formPhases.success);
        } else {
            setFormPhase(formPhases.error);
        }
    }

    const addPosts = async () => {
        const {text, ...rest} = data;
        const postData = Object.assign(rest, {text: encodeURI(text)});

        if (images.length) {
            const compressedImages = await images.map(async (e) => compressImage(e));
            const compressedImagesResolved = await Promise.all(compressedImages);
            const imagesArr = await Promise.all(compressedImagesResolved.map(e => convertFileToBase64(e)));
            return await addData('posts', {...postData, date: Date.now(), images: imagesArr});
        } else {
            return await addData('posts', {...postData, date: Date.now()});
        }
    }

     const convertFileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
        });
    }

    const compressImage = async (image) => {
        const options = {
            maxSizeMB: 0.5,
            maxWidthOrHeight: 1920,
            useWebWorker: true
        }

        try {
            return await imageCompression(image, options);
        } catch (error) {
            console.error(error);
        }
    }

    const addPlayers = async () => {
        const {date_of_birth, ...playerData} = data;
        const playerPayload = date_of_birth ?
            Object.assign(playerData, {date_of_birth: new Date(date_of_birth).getTime()}) : playerData;

        return await addData('players', playerPayload);
    }

    const addMatches = async () => {
        const {round, date, enemy_goals, ...matchData} = data;
        const clubGoals = goalsInfo.reduce((acc, stat) => acc + stat.goals, 0);
        const matchPayload = Object.assign(matchData, {
            round: round ? Number(round) : 0, club_goals: clubGoals,
            enemy_goals: enemy_goals ? Number(enemy_goals) : 0, date: new Date(date).getTime()});

        const result = await addData('matches', matchPayload);
        const match = await result.json();

        const goalsStats = goalsInfo.map(e => ({...e, match_id: match.id}));
        return Promise.all(goalsStats.map(async (e) => await addData('statistics', e)));
    };

    const addTournaments = async () => {
        const {start_date, end_date, ...tournamentData} = data;
        const tournamentPayload = Object.assign(tournamentData, {
            start_date: new Date(start_date).getTime(),
            end_date: new Date(end_date).getTime()
        })

        return addData('tournaments', tournamentPayload);
    }

    const validateData = () => {
        switch (formType) {
            case 'posts':
                return data.title && data.text;
            case 'players':
                return data.name && data.surname && data.position;
            case 'matches':
                return data.tournament_id && data.date && data.enemy_name && goalsInfo.length;
            case 'tournaments':
                return data.name && data.venue && data.start_date && data.end_date && data.place && (data.start_date <= data.end_date);
            default:
                return true;
        }
    }

    const clearData = () => {
        setFormPhase(formPhases.initial);
        setImages([]);

        switch (formType) {
            case 'players':
                setData(defaultData.players);
                break;
            case 'tournaments':
                setData(defaultData.tournaments);
                break;
            case 'matches':
                setData(defaultData.matches);
                setGoalsInfo([]);
                break;
            default:
                setData({});
        }
    }

    const renderPlaces = () => {
        const placeTypes = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '3-4', '5-8', '9-16'];
        return placeTypes.map(e =>
            <StyledOption key={e} value={e}>{e}</StyledOption>
        );
    }

    const renderTournamentsList = () => {
        let view = [];
        view.push(<StyledOption disabled key={'default'} value={'default'}>{'Wybierz turniej'}</StyledOption>);

        for(let i=0; i<tournamentsList.length; i++) {
            view.push(
                <StyledOption key={tournamentsList[i].id} value={tournamentsList[i].id}>
                    {tournamentsList[i].name}
                </StyledOption>
            );
        }

        return view;
    }

    const renderRounds = () =>
        rounds.map((e, index) => <StyledOption key={index} value={index}>{e}</StyledOption>);

    const renderPlayers = () =>
        playerList.map(e => <StyledOption key={e.id} value={e.id}>{e.name + ' ' + e.surname}</StyledOption>);

    const renderGoalsInfoSelector = () => {
        return goalsInfo.map((e, index) => {
            return (
                <StyledInputRow key={index}>
                    <FormSelect onChange={(event) => setGoalsInfo(
                        goalsInfo.map((e, idx) => idx === index ? {...e, player_id: event.target.value} : e))}
                    >
                        {renderPlayers()}
                    </FormSelect>

                    <FormInput onChange={(event) => setGoalsInfo(
                        goalsInfo.map((e, idx) => idx === index ? {...e, goals: Number(event.target.value)} : e))}
                               width={'30%'} type={'number'} min={0} value={e.goals}
                               style={{marginLeft: 15}}
                    />

                    <StyledButton onClick={() => setGoalsInfo(goalsInfo.filter((e, idx) => idx !== index))}>
                        {'-'}
                    </StyledButton>
                </StyledInputRow>
            );
        });
    }

    const renderForm = () => {
        switch (formType) {
            case 'posts':
                return (
                    <>
                        <StyledRow>
                            <FormLabel htmlFor={'title'}>{'Tytuł: '}</FormLabel>
                            <FormInput required id={'title'} value={data.title ?? ''}
                                       onChange={(event) => setData({...data, title: event.target.value})}
                            />
                        </StyledRow>
                        <StyledRow flexDirection={'column'}>
                            <FormLabel htmlFor={'text'}>{'Treść: '}</FormLabel>
                            <StyledTextArea required id={'text'} value={data.text ?? ''}
                                            onChange={(event) => setData({...data, text: event.target.value})}
                            />
                        </StyledRow>
                        <StyledRow flexDirection={'column'}>
                            <FormLabel htmlFor={'images'} type={'text'}>{'Obrazki: '}</FormLabel>
                            <FileInput id={'images'} onChange={(event) => setImages([...images, ...event.target.files])}
                                       type={'file'} name={'files[]'} accept={'image/*'} multiple/>
                            <ImgPreviewContainer>{renderImagesPreview()}</ImgPreviewContainer>
                        </StyledRow>
                    </>
                )
            case 'players':
                return (
                    <>
                        <StyledRow>
                            <FormLabel htmlFor={'name'}>{'Imię: '}</FormLabel>
                            <FormInput required id={'name'} onChange={(event) => setData({...data, name: event.target.value})}
                                       value={data.name ?? ''}
                            />
                        </StyledRow>
                        <StyledRow>
                            <FormLabel htmlFor={'surname'}>{'Nazwisko: '}</FormLabel>
                            <FormInput required id={'surname'} onChange={(event) => setData({...data, surname: event.target.value})}
                                       value={data.surname ?? ''}
                            />
                        </StyledRow>
                        <StyledRow>
                            <FormLabel htmlFor={'date'}>{'Data urodzenia: '}</FormLabel>
                            <FormInput id={'date'} onChange={(event) => setData({...data, date_of_birth: event.target.value})}
                                       value={data.date_of_birth ?? ''}
                                       type={'date'}
                            />
                        </StyledRow>
                        <StyledRow>
                            <FormLabel htmlFor={'position'}>{'Pozycja: '}</FormLabel>
                            <FormSelect required id={'position'}
                                        onChange={(event) => setData({...data, position: event.target.value})}
                                        value={data.position}>
                                <StyledOption key={'goalkeeper'} value={'goalkeeper'}>{'bramkarz'}</StyledOption>
                                <StyledOption key={'striker'} value={'striker'}>{'napastnik'}</StyledOption>
                                <StyledOption key={'defender'} value={'defender'}>{'obrońca'}</StyledOption>
                                <StyledOption key={'midfielder'} value={'midfielder'}>{'pomocnik'}</StyledOption>
                            </FormSelect>
                        </StyledRow>
                        <StyledRow>
                            <FormLabel htmlFor={'country'}>{'Kraj pochodzenia: '}</FormLabel>
                            <FormSelect required id={'country'}
                                        onChange={(event) => setData({...data, country: event.target.value})}
                                        value={data.country}>
                                <StyledOption key={'pl'} value={'pl'}>{'Polska'}</StyledOption>
                            </FormSelect>
                        </StyledRow>
                    </>
                )
            case 'matches':
                return (
                    <>
                        <StyledRow>
                            <FormLabel htmlFor={'tournament'} type={'text'}>{'Turniej: '}</FormLabel>
                            <FormSelect required id={'tournament'} defaultValue={'default'}
                                        onChange={(event) => setData({...data, tournament_id: event.target.value})}
                                        value={data.tournament_id}>
                                {renderTournamentsList()}
                            </FormSelect>
                        </StyledRow>
                        <StyledRow>
                            <FormLabel htmlFor={'round'}>{'Faza: '}</FormLabel>
                            <FormSelect required id={'round'}
                                        onChange={(event) => setData({...data, round: event.target.value})}
                                        value={data.round ?? 0}>
                                {renderRounds()}
                            </FormSelect>
                        </StyledRow>
                        <StyledRow>
                            <FormLabel htmlFor={'date'} type={'text'}>{'Data (YYYY-MM-DD HH:mm): '}</FormLabel>
                            <FormInput id={'date'} required onChange={(event) => setData({...data, date: event.target.value})}
                                       value={data.date ?? ''} width={'40%'}
                            />
                        </StyledRow>
                        <StyledRow>
                            <FormLabel htmlFor={'enemy_name'} type={'text'}>{'Drużyna (przeciwnicy): '}</FormLabel>
                            <FormInput id={'enemy_name'} required
                                       onChange={(event) => setData({...data, enemy_name: event.target.value})}
                                       value={data.enemy_name ?? ''} width={'50%'}
                            />
                        </StyledRow>
                        <StyledRow>
                            <FormLabel htmlFor={'enemy_goals'} type={'text'}>{'Liczba goli (przeciwnicy): '}</FormLabel>
                            <FormInput id={'enemy_goals'} required
                                       onChange={(event) => setData({...data, enemy_goals: Number(event.target.value)})}
                                       value={data.enemy_goals ?? 0} width={'10%'} type={'number'} min={0}
                            />
                        </StyledRow>
                        <StyledRow flexDirection={'column'}>
                            <FormLabel htmlFor={'club_goals'} type={'text'}>{'Gole (drużyna): '}</FormLabel>
                            {renderGoalsInfoSelector()}
                            <StyledButton id={'club_goals'}
                                onClick={() => setGoalsInfo(prevGoalsInfo =>
                                    [...prevGoalsInfo, {player_id: playerList[0].id, goals: 0}])}>
                                {'+'}
                            </StyledButton>
                        </StyledRow>
                        <StyledRow flexDirection={'column'}>
                            <FormLabel htmlFor={'comment'} type={'text'}>{'Komentarz do meczu: '}</FormLabel>
                            <StyledTextArea id={'comment'} height={'80px'}
                                            onChange={(event) => setData({...data, comment: event.target.value})}
                                            value={data.comment ?? ''}/>
                        </StyledRow>
                    </>
                )
            case 'tournaments':
                return (
                    <>
                        <StyledRow>
                            <FormLabel htmlFor={'name'}>{'Nazwa turnieju: '}</FormLabel>
                            <FormInput id={'name'} required
                                       onChange={(event) => setData({...data, name: event.target.value})}
                                       value={data.name ?? ''}
                            />
                        </StyledRow>
                        <StyledRow>
                            <FormLabel htmlFor={'venue'}>{'Lokalizacja: '}</FormLabel>
                            <FormInput id={'venue'} required
                                       onChange={(event) => setData({...data, venue: event.target.value})}
                                       value={data.venue ?? ''}
                            />
                        </StyledRow>
                        <StyledRow>
                            <FormLabel htmlFor={'start_date'}>{'Data rozpoczęcia: '}</FormLabel>
                            <FormInput id={'start_date'} required width={'30%'}
                                       onChange={(event) => setData({...data, start_date: event.target.value})}
                                       value={data.start_date ?? ''} type={'date'}
                            />
                        </StyledRow>
                        <StyledRow>
                            <FormLabel htmlFor={'end_date'}>{'Data zakończenia: '}</FormLabel>
                            <FormInput id={'end_date'} required width={'30%'}
                                       onChange={(event) => setData({...data, end_date: event.target.value})}
                                       value={data.end_date ?? ''} type={'date'}
                            />
                        </StyledRow>
                        <StyledRow>
                            <FormLabel htmlFor={'place'}>{'Zajęta pozycja: '}</FormLabel>
                            <FormSelect id={'place'} required
                                        onChange={(event) => setData({...data, place: event.target.value})}
                                        value={data.place}>
                                {renderPlaces()}
                            </FormSelect>
                        </StyledRow>
                    </>
                )
            default:
                return (<FormContent/>);
        }
    }

    return (
        <StyledForm>
            <FormHeader>{formInfo[formType].header}</FormHeader><br/>
            <FontAwesomeIcon icon={formInfo[formType].icon} color={'black'}
                             style={{fontSize: 40, position: 'absolute', right: 40, top: 20}}/>
            <FormContent>
                {renderForm()}
                <FormSubheader>{`Wypełnij wszystkie wymagane pola, a następnie naciśnij wybierz przycisk Dodaj lub Wyczyść. Wynik operacji zostanie przedstawiony w statusie formularza. W przypadku dodawania zdjęć, pierwsze widoczne zdjęcie w podglądzie jest zdjęciem głównym.`}</FormSubheader>
                <ButtonsContainer>
                    <FormButton onClick={clearData} type={'button'}
                                butType={'clear'}>{'Wyczyść'}</FormButton>
                    <FormButton disabled={!validateData()} onClick={confirmData} type={'button'}
                                butType={'add'}>{'Dodaj'}</FormButton>
                </ButtonsContainer>
                <FormSubheader>{`Aktualny status formularza: `}
                    <ColoredStatus color={formPhase.color}>{formPhase.text}</ColoredStatus>
                </FormSubheader>
            </FormContent>
        </StyledForm>
    );
};

ManageDataForm.propTypes = {
    formType: PropTypes.string.isRequired
}

export default ManageDataForm;
